import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { StoreProvider } from './store/useStore';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { AIRecoveryChatModal } from './components/ai/AIRecoveryChatModal';

// Core Pages
import { Home } from './pages/Home';
import { CheckIdentity } from './pages/CheckIdentity';
import { SecurityScorePage } from './pages/SecurityScore';
import { Dashboard } from './pages/Dashboard';
import { AlertsPage } from './pages/Alerts';
import { ScamCheckerPage } from './pages/ScamChecker';
import { WebsiteCheckerPage } from './pages/WebsiteChecker';
import { IdentityTheftPage } from './pages/IdentityTheft';
import { RecoveryWizardPage } from './pages/RecoveryWizard';
import { IdentityDocumentsPage } from './pages/IdentityDocuments';
import { IdWatermarkPage } from './pages/IdWatermark';
import { AccountSecurityPage } from './pages/AccountSecurity';
import { FamilyProtectionPage } from './pages/FamilyProtection';
import { PricingPage } from './pages/Pricing';
import { ResourcesPage } from './pages/Resources';
import { ResourceDetailPage } from './pages/ResourceDetail';
import { PrivacyPage } from './pages/Privacy';
import { HowItWorksPage } from './pages/HowItWorks';
import { LoginPage } from './pages/Login';
import { RegisterPage } from './pages/Register';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { SettingsPage } from './pages/Settings';
import { AdminDashboardPage } from './pages/AdminDashboard';

// 20 Unique Tools & Features
import { ToolsDirectoryPage } from './pages/ToolsDirectory';
import { PhoneCheckerPage } from './pages/tools/PhoneChecker';
import { BsbCheckerPage } from './pages/tools/BsbChecker';
import { AbnCheckerPage } from './pages/tools/AbnChecker';
import { QrScannerPage } from './pages/tools/QrScanner';
import { BreachCalculatorPage } from './pages/tools/BreachCalculator';
import { CreditBanGeneratorPage } from './pages/tools/CreditBanGenerator';
import { ScamSimulatorPage } from './pages/tools/ScamSimulator';
import { DigitalEstatePage } from './pages/tools/DigitalEstate';
import { PasswordAnalyzerPage } from './pages/tools/PasswordAnalyzer';
import { RentalShieldPage } from './pages/tools/RentalShield';
import { DarkWebFeedPage } from './pages/tools/DarkWebFeed';
import { ThreatIntelPage } from './pages/tools/ThreatIntel';
import { FamilyConsentPage } from './pages/tools/FamilyConsent';
import { EmailHeaderAnalyzerPage } from './pages/tools/EmailHeaderAnalyzer';
import { OaicComplaintGeneratorPage } from './pages/tools/OaicComplaintGenerator';
import { DeepfakeAudioCheckerPage } from './pages/tools/DeepfakeAudioChecker';
import { MedicareShieldPage } from './pages/tools/MedicareShield';
import { InsuranceComparatorPage } from './pages/tools/InsuranceComparator';
import { TeenShieldPage } from './pages/tools/TeenShield';
import { IncidentExporterPage } from './pages/tools/IncidentExporter';

// Scroll to top on navigation
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const App: React.FC = () => {
  return (
    <StoreProvider>
      <Router>
        <ScrollToTop />
        <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans selection:bg-emerald-500 selection:text-white">
          <Navbar />
          <main className="flex-1">
            <Routes>
              {/* Primary Pages */}
              <Route path="/" element={<Home />} />
              <Route path="/check-identity" element={<CheckIdentity />} />
              <Route path="/security-score" element={<SecurityScorePage />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/scam-checker" element={<ScamCheckerPage />} />
              <Route path="/website-checker" element={<WebsiteCheckerPage />} />
              <Route path="/identity-theft" element={<IdentityTheftPage />} />
              <Route path="/recovery-wizard" element={<RecoveryWizardPage />} />
              <Route path="/identity-documents" element={<IdentityDocumentsPage />} />
              <Route path="/id-watermark" element={<IdWatermarkPage />} />
              <Route path="/account-security" element={<AccountSecurityPage />} />
              <Route path="/family" element={<FamilyProtectionPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/resources/:slug" element={<ResourceDetailPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/admin" element={<AdminDashboardPage />} />

              {/* 20 Unique Tools & Security Utilities */}
              <Route path="/tools" element={<ToolsDirectoryPage />} />
              <Route path="/phone-lookup" element={<PhoneCheckerPage />} />
              <Route path="/bsb-checker" element={<BsbCheckerPage />} />
              <Route path="/abn-checker" element={<AbnCheckerPage />} />
              <Route path="/qr-scanner" element={<QrScannerPage />} />
              <Route path="/breach-calculator" element={<BreachCalculatorPage />} />
              <Route path="/credit-ban-generator" element={<CreditBanGeneratorPage />} />
              <Route path="/scam-simulator" element={<ScamSimulatorPage />} />
              <Route path="/digital-estate" element={<DigitalEstatePage />} />
              <Route path="/password-analyzer" element={<PasswordAnalyzerPage />} />
              <Route path="/rental-shield" element={<RentalShieldPage />} />
              <Route path="/darkweb-feed" element={<DarkWebFeedPage />} />
              <Route path="/threat-intel" element={<ThreatIntelPage />} />
              <Route path="/family-consent" element={<FamilyConsentPage />} />
              <Route path="/email-header-analyzer" element={<EmailHeaderAnalyzerPage />} />
              <Route path="/oaic-complaint-generator" element={<OaicComplaintGeneratorPage />} />
              <Route path="/deepfake-audio-checker" element={<DeepfakeAudioCheckerPage />} />
              <Route path="/medicare-shield" element={<MedicareShieldPage />} />
              <Route path="/cyber-insurance-comparator" element={<InsuranceComparatorPage />} />
              <Route path="/teen-shield" element={<TeenShieldPage />} />
              <Route path="/incident-exporter" element={<IncidentExporterPage />} />

              {/* Fallback */}
              <Route path="*" element={<Home />} />
            </Routes>
          </main>
          <Footer />
          <AIRecoveryChatModal />
        </div>
      </Router>
    </StoreProvider>
  );
};
